import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { FormattedMessage } from 'react-intl';
import { ContentBody } from '../atoms/ContentBody/ContentBody';
import { ProviderField } from '../../models/provider';
import { SubmitProviderInputsPayload } from '../screens/SubmitCredentials/molecules/ProviderInputs/ProviderInputs';
import { Text } from '../atoms/Text/Text';
import { H1 } from '../atoms/Heading/Heading';
import { usePageViewEvents } from '../../core/Analytics/usePageViewEvents';
import { ApplicationEvent, PageView, UserInteractionEvent } from '../../models/analytics';
import { Footer } from '../atoms/Footer/Footer';
import { useAnalytics } from '../../core/Analytics/AnalyticsProvider';
import { Button } from '../atoms/Button/Button';
import { useCredentialsCopy } from '../screens/SubmitCredentials/hooks/useCredentialsCopy';
import { messages } from './messages';
import { TemplateElementStyle, TemplateElementType } from './templates';
import { MaybeChangeMethodButton } from './MaybeChangeMethodButton/MaybeChangeMethodButton';

export interface QrCodeTemplateProps {
  fields: ProviderField[];
  onSubmit: (values: SubmitProviderInputsPayload, isAutoSubmit?: boolean) => void;
}

export const QrCodeTemplate = ({ fields, onSubmit }: QrCodeTemplateProps) => {
  const { sendViewInteractionEvent } = usePageViewEvents({
    pageView: PageView.QR_CODE_TEMPLATE_SCREEN,
  });

  const handleSubmitWithAnalytics = useCallback(
    (values: SubmitProviderInputsPayload, isAutoSubmit?: boolean) => {
      sendViewInteractionEvent({
        interaction: UserInteractionEvent.SUBMIT_SUPPLEMENTAL_INFORMATION,
      });
      onSubmit(values, isAutoSubmit);
    },
    [onSubmit, sendViewInteractionEvent]
  );

  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);
  const { tinklinkAnalytics } = useAnalytics();
  const { heading } = useCredentialsCopy();

  const parsedElements = useMemo(() => {
    const title = fields.find(
      (el) => el.type === TemplateElementType.TEXT && el.style === TemplateElementStyle.TITLE
    );

    const helpText = fields.find(
      (el) =>
        el.type === TemplateElementType.TEXT && el.style === TemplateElementStyle.IDENTITY_HINT
    );

    const qrCode = fields.find(
      (el) => el.type === TemplateElementType.IMAGE && el.style === TemplateElementStyle.IMAGE
    );

    const changeMethodElement = fields.find(
      (elm) => elm.type === TemplateElementType.CHANGE_METHOD
    );

    return { title, qrCode, helpText, changeMethodElement };
  }, [fields]);

  useEffect(() => {
    if (!hasAutoSubmitted && !parsedElements.changeMethodElement) {
      handleSubmitWithAnalytics({}, true);
      setHasAutoSubmitted(true);

      tinklinkAnalytics.sendApplicationEvent(ApplicationEvent.QR_CODE_AUTO_SUBMIT);
    }
  }, [
    handleSubmitWithAnalytics,
    hasAutoSubmitted,
    setHasAutoSubmitted,
    parsedElements.changeMethodElement,
    tinklinkAnalytics,
  ]);

  const handleUserHasAuthenticated = () => {
    handleSubmitWithAnalytics({});
    tinklinkAnalytics.sendApplicationEvent(ApplicationEvent.QR_CODE_USER_SUBMIT);
  };

  return (
    <ContentBody data-testid="qr-code-template-screen">
      <Box>
        <H1>{heading}</H1>
        {parsedElements.title && <Text color="secondary">{parsedElements.title.value}</Text>}

        {parsedElements.qrCode?.value && (
          <Box my="large" textAlign="center">
            <img src={parsedElements.qrCode.value} alt="qr-code" />
          </Box>
        )}
      </Box>
      {parsedElements.changeMethodElement && (
        <Footer stickToBottom px="none">
          <Button
            type="submit"
            color="primary"
            variant="contained"
            onClick={handleUserHasAuthenticated}
          >
            <FormattedMessage {...messages.decoupledTemplateContinue} />
          </Button>
          <MaybeChangeMethodButton elements={fields} onSubmit={onSubmit} />
        </Footer>
      )}
    </ContentBody>
  );
};
